import React, { useState, useEffect } from "react";
import Header from "../../components/ui/Header";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Icon from "../../components/AppIcon";
import EntityTable from "./components/EntityTable";
import EntityFormModal from "./components/EntityFormModal";
import EntityViewModal from "./components/EntityViewModal";
import DeleteConfirmModal from "../users-crud-management/components/DeleteConfirmModal";
import { useSelector } from "react-redux";

const WorkshopsAgenciesManagementHub = () => {
  const { metaWorkshops } = useSelector((state) => state.workshops);
  const [language, setLanguage] = useState("ar");
  const [activeTab, setActiveTab] = useState("workshops");
  const [workshops, setWorkshops] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [formMode, setFormMode] = useState("add");

  useEffect(() => {
    const savedLanguage = localStorage.getItem("language") || "en";
    setLanguage(savedLanguage);

    // Mock data for workshops
    setWorkshops([
      {
        id: 1,
        name: "Premium Auto Workshop",
        images: [
          "https://img.rocket.new/generatedImages/rocket_gen_img_18811c304-1763296452128.png",
        ],
        location: { lat: 25.2048, lng: 55.2708, address: "Dubai, UAE" },
        serviceTypes: ["Oil Change", "Brake Repair", "Engine Diagnostics"],
      },
      {
        id: 2,
        name: "Quick Fix Garage",
        images: [
          "https://img.rocket.new/generatedImages/rocket_gen_img_1fe8a4833-1763295664840.png",
        ],
        location: { lat: 24.4539, lng: 54.3773, address: "Abu Dhabi, UAE" },
        serviceTypes: ["Tire Replacement", "AC Repair"],
      },
    ]);

    // Mock data for agencies
    setAgencies([
      {
        id: 1,
        name: "Elite Car Rentals",
        images: [
          "https://img.rocket.new/generatedImages/rocket_gen_img_128f7e26a-1763297659639.png",
        ],
        location: { lat: 25.3548, lng: 51.1839, address: "Doha, Qatar" },
        serviceTypes: ["Car Rental", "Chauffeur Service", "Airport Transfer"],
      },
      {
        id: 2,
        name: "Luxury Auto Agency",
        images: [
          "https://img.rocket.new/generatedImages/rocket_gen_img_18811c304-1763296452128.png",
        ],
        location: { lat: 25.2048, lng: 55.2708, address: "Dubai, UAE" },
        serviceTypes: ["Luxury Rentals", "Long-term Lease"],
      },
    ]);
  }, []);

  const labels = {
    title: {
      en: "Workshops & Agencies Management",
      ar: "إدارة الورش والوكالات",
    },
    workshopsTab: { en: "Workshops", ar: "الورش" },
    agenciesTab: { en: "Agencies", ar: "الوكالات" },
    addWorkshop: { en: "Add New Workshop", ar: "إضافة ورشة جديدة" },
    addAgency: { en: "Add New Agency", ar: "إضافة وكالة جديدة" },
    searchWorkshops: { en: "Search workshops...", ar: "البحث عن الورش..." },
    searchAgencies: { en: "Search agencies...", ar: "البحث عن الوكالات..." },
    noWorkshops: { en: "No workshops found", ar: "لم يتم العثور على ورش" },
    noAgencies: { en: "No agencies found", ar: "لم يتم العثور على وكالات" },
  };

  const currentData = activeTab === "workshops" ? workshops : agencies;
  const setCurrentData = activeTab === "workshops" ? setWorkshops : setAgencies;

  const handleAddEntity = () => {
    setFormMode("add");
    setSelectedEntity(null);
    setIsFormModalOpen(true);
  };

  const handleEditEntity = (entity) => {
    setFormMode("edit");
    setSelectedEntity(entity);
    setIsFormModalOpen(true);
  };

  const handleViewEntity = (entity) => {
    setSelectedEntity(entity);
    setIsViewModalOpen(true);
  };

  const handleDeleteEntity = (entity) => {
    setSelectedEntity(entity);
    setIsDeleteModalOpen(true);
  };

  const handleSaveEntity = (entityData) => {
    if (formMode === "add") {
      const newEntity = { ...entityData, id: Date.now() };
      setCurrentData([...currentData, newEntity]);
    } else {
      setCurrentData(
        currentData?.map((e) =>
          e?.id === selectedEntity?.id ? { ...e, ...entityData } : e,
        ),
      );
    }
    setIsFormModalOpen(false);
  };

  const handleConfirmDelete = () => {
    setCurrentData(currentData?.filter((e) => e?.id !== selectedEntity?.id));
    setIsDeleteModalOpen(false);
  };

  const filteredData = currentData?.filter(
    (entity) =>
      entity?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      entity?.location?.address
        ?.toLowerCase()
        ?.includes(searchQuery?.toLowerCase()) ||
      entity?.serviceTypes?.some((service) =>
        service?.toLowerCase()?.includes(searchQuery?.toLowerCase()),
      ),
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            {labels?.title?.[language]}
       
          </h1>
  
        </div>

        <div className="bg-card rounded-xl shadow-elevation-2 border border-border overflow-hidden">
          <div className="border-b border-border">
            <div className="flex">
              <button
                onClick={() => {
                  setActiveTab("workshops");
                  setSearchQuery("");
                }}
                className={`flex-1 px-6 py-4 text-sm font-semibold transition-smooth relative ${
                  activeTab === "workshops"
                    ? "text-primary bg-primary/5"
                    : "text-text-secondary hover:bg-muted/50"
                }`}
              >
                <Icon name="Wrench" size={18} className="inline-block mr-2" />
                {labels?.workshopsTab?.[language]}
                {activeTab === "workshops" && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
              <button
                onClick={() => {
                  setActiveTab("agencies");
                  setSearchQuery("");
                }}
                className={`flex-1 px-6 py-4 text-sm font-semibold transition-smooth relative ${
                  activeTab === "agencies"
                    ? "text-primary bg-primary/5"
                    : "text-text-secondary hover:bg-muted/50"
                }`}
              >
                <Icon
                  name="Building2"
                  size={18}
                  className="inline-block mr-2"
                />
                {labels?.agenciesTab?.[language]}
                {activeTab === "agencies" && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
            </div>
          </div>

          <div className="p-4 md:p-6">
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder={
                    activeTab === "workshops"
                      ? labels?.searchWorkshops?.[language]
                      : labels?.searchAgencies?.[language]
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full"
                />
              </div>
              <Button
                onClick={handleAddEntity}
                iconName="Plus"
                variant="default"
              >
                {activeTab === "workshops"
                  ? labels?.addWorkshop?.[language]
                  : labels?.addAgency?.[language]}
              </Button>
            </div>

            {filteredData?.length === 0 ? (
              <div className="text-center py-12">
                <Icon
                  name={activeTab === "workshops" ? "Wrench" : "Building2"}
                  size={48}
                  className="mx-auto text-muted-foreground mb-4"
                />
                <p className="text-text-secondary">
                  {activeTab === "workshops"
                    ? labels?.noWorkshops?.[language]
                    : labels?.noAgencies?.[language]}
                </p>
              </div>
            ) : (
              <EntityTable
                entities={filteredData}
                entityType={activeTab}
                language={language}
                onEdit={handleEditEntity}
                onDelete={handleDeleteEntity}
                onView={handleViewEntity}
              />
            )}
          </div>
        </div>
      </div>

      {isFormModalOpen && (
        <EntityFormModal
          isOpen={isFormModalOpen}
          onClose={() => setIsFormModalOpen(false)}
          onSave={handleSaveEntity}
          entity={selectedEntity}
          entityType={activeTab}
          mode={formMode}
          language={language}
        />
      )}

      {isViewModalOpen && (
        <EntityViewModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          entity={selectedEntity}
          entityType={activeTab}
          language={language}
          onEdit={() => {
            setIsViewModalOpen(false);
            handleEditEntity(selectedEntity);
          }}
          onDelete={() => {
            setIsViewModalOpen(false);
            handleDeleteEntity(selectedEntity);
          }}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          itemName={selectedEntity?.name}
          language={language}
        />
      )}
    </div>
  );
};

export default WorkshopsAgenciesManagementHub;
